import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { AbstractBean } from '@app/core/model/abstract-bean';
import { Expose, Transform, Type } from 'class-transformer';
import dayjs, { Dayjs } from 'dayjs';

export abstract class Entity extends AbstractBean {

  @Transform(fn => dayjs(fn.value), {toClassOnly: true})
  @Type(() => String)
  public creationDate: Dayjs;
  @Transform(fn => dayjs(fn.value), {toClassOnly: true})
  @Type(() => String)
  public lastChangeDate: Dayjs;
  public properties: Record<string, any>;
  public computedProperties: Record<string, any>;

  @Type(() => Actor)
  public creationActor: Actor;
  @Type(() => Actor)
  public lastChangeActor: Actor;

  protected static inputMapping: Record<string, InputMapping>;

  /**
   * Apply transformation to an Entity update input before calling the API.
   * Do nothing by default, specifics behaviors are implemented in child classes.
   * @param _
   */
  public static transformPropertiesForInput(_: Record<string, unknown>): void {
    // Custom behavior in child classes
  }
}

export interface InputMapping {
  input: string[];
  output: string;
  transform?: (output: any) => any;
}

export interface IRelatedEntity {
  id: string;
  name?: string;
  identifier?: number | string;
  displayName?: string;
}

export class RelatedEntity implements IRelatedEntity {
  public id: string;
  public displayName: string;

  constructor(relatedEntity: IRelatedEntity) {
    this.id = relatedEntity.id;
    this.displayName = relatedEntity.displayName
      ?? `${relatedEntity.identifier ?? '?'} - ${relatedEntity.name ?? '?'}`;
  }

  @Expose()
  public toString(): string {
    return this.displayName;
  }

  /**
   * Fields' types that instantiate related entity.
   */
  public static readonly relatedEntityFields = [
    FieldTypeEnum.ASSET,
    FieldTypeEnum.ASSETS,
    FieldTypeEnum.DOCUMENT_RELATED_ASSETS,
    FieldTypeEnum.MEETING,
    FieldTypeEnum.EQUIPMENTS,
    FieldTypeEnum.PROJECTS,
    FieldTypeEnum.SPACES
  ];
}

/**
 * Information about an entity, user or client, who has been involved in the creation or update of another entity.
 */
export class Actor {
  constructor(public readonly actorId: string, public name?: string) {}

  /**
   * String representation of this actor, i.e. its name.
   * @return Actor's name.
   */
  public toString() {
    return this.name;
  }

  /**
   * Equality check between actors.
   * @param actor The actor to compare with the current instance.
   * @return True if the actor IDs are equal, otherwise false.
   */
  public equals(actor: Actor): boolean {
    return this.actorId === actor.actorId;
  }
}

export type EntityInput = {entity: Entity, entityType: EntityTypeEnum};
